import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Image,
  Link,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from "@chakra-ui/react";
import PageHeader from "../components/PageHeader";
import { formatKickoff as fmtKickoff } from "../utils/fixtureStatus";
import MatchupHistoryModal from "../components/MatchupHistoryModal";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  getSurvivorPool,
  getMyEntries,
  createEntry,
  getMySurvivorPicks,
  getAvailableTeams,
  getNflWeekGames,
  getNflCurrentWeek,
  getNflStandings,
  getNflMatchupHistory,
  makeSurvivorPick,
  getBuybackStatus,
  buyBackEntry,
} from "../api/nflSurvivor";
import { useNflSeason } from "../hooks/useNflSeason";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE = "#00007f";
// ── Team logo size on the pick cards — adjust here ───────────────────────
const LOGO_SIZE = { base: "58px", md: "82px" };

/** One selectable team on a matchup card: logo, abbreviation and odds. */
function TeamPick({ team, selected, disabled, used, odds, homeAway, onSelect }) {
  if (!team) return null;
  return (
    <Tooltip hasArrow isDisabled={!used} label="You've already used this team this season">
      <VStack
        spacing={1}
        as="button"
        type="button"
        onClick={disabled ? undefined : onSelect}
        cursor={disabled ? "not-allowed" : "pointer"}
        opacity={disabled && !selected ? 0.4 : 1}
        transition="transform 0.12s ease"
        _hover={disabled ? undefined : { transform: "scale(1.06)" }}
        flex="1"
      >
        <Text fontSize="0.6rem" color="gray.400" textTransform="uppercase" letterSpacing="0.5px">
          {homeAway}
        </Text>
        {team.logo_url ? (
          <Image
            src={team.logo_url}
            alt={team.short_name}
            boxSize={LOGO_SIZE}
            objectFit="contain"
            opacity={selected ? 1 : 0.9}
          />
        ) : (
          <Flex boxSize={LOGO_SIZE} align="center" justify="center">
            <Text fontSize="lg" fontWeight="bold" color="gray.400">{team.short_name}</Text>
          </Flex>
        )}
        <Text
          fontSize="sm"
          fontWeight={selected ? "bold" : "semibold"}
          color={selected ? TOP_BAR_BLUE : "gray.700"}
          bg={selected ? "yellow.300" : "transparent"}
          px={1}
          borderRadius="sm"
        >
          {team.short_name}
        </Text>
        <Text fontSize="0.6rem" color="gray.500" minH="14px">
          {odds != null ? odds : ""}
        </Text>
      </VStack>
    </Tooltip>
  );
}


export default function NflSurvivorPicksPage() {
  const { poolId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { seasonId: hookSeasonId } = useNflSeason();
  const SEASON_ID = location.state?.seasonId ?? hookSeasonId;
  const numericPoolId = Number(poolId);

  const [pool, setPool] = useState(null);
  const [entries, setEntries] = useState([]);
  const [activeEntryId, setActiveEntryId] = useState(null);
  const [week, setWeek] = useState(null);
  const [maxWeek, setMaxWeek] = useState(18);
  const [weekData, setWeekData] = useState(null);
  const [available, setAvailable] = useState([]);
  const [myPicks, setMyPicks] = useState([]);
  const [buyback, setBuyback] = useState(null);
  const [standings, setStandings] = useState(null);
  const [historyGame, setHistoryGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (!SEASON_ID || !numericPoolId) return;
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SEASON_ID, numericPoolId]);

  async function init() {
    setLoading(true);
    try {
      const [poolData, myEntries, picks, weekMeta, standingsData] = await Promise.all([
        getSurvivorPool(SEASON_ID, numericPoolId),
        getMyEntries(SEASON_ID, numericPoolId),
        getMySurvivorPicks(SEASON_ID, numericPoolId).catch(() => []),
        getNflCurrentWeek(SEASON_ID).catch(() => null),
        getNflStandings(SEASON_ID).catch(() => null),
      ]);
      setPool(poolData);
      setEntries(myEntries);
      setStandings(standingsData);
      setMyPicks(picks);
      const firstEntry = myEntries[0]?.id ?? null;
      setActiveEntryId(firstEntry);
      const wk = weekMeta?.current_week ?? 1;
      setMaxWeek(weekMeta?.max_week ?? 18);
      setWeek(wk);
      if (firstEntry) await loadWeek(wk, firstEntry);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load picks page.");
    } finally {
      setLoading(false);
    }
  }

  async function loadWeek(wk, entryId) {
    setError("");
    try {
      const [games, avail, bb] = await Promise.all([
        getNflWeekGames(SEASON_ID, wk),
        getAvailableTeams(SEASON_ID, numericPoolId, entryId, wk).catch(() => ({ available_team_ids: [] })),
        getBuybackStatus(SEASON_ID, numericPoolId, entryId, wk).catch(() => null),
      ]);
      setWeekData(games);
      setAvailable(avail.available_team_ids || []);
      setBuyback(bb);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load week.");
    }
  }

  const activeEntry = useMemo(
    () => entries.find((e) => e.id === activeEntryId) || null,
    [entries, activeEntryId]
  );

  const pickForWeek = useMemo(
    () => myPicks.find((p) => p.entry_id === activeEntryId && p.week === week) || null,
    [myPicks, activeEntryId, week]
  );

  const availableSet = useMemo(() => new Set(available), [available]);

  async function handlePick(game, teamId) {
    if (!activeEntryId) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      await makeSurvivorPick(SEASON_ID, numericPoolId, {
        entry_id: activeEntryId, game_id: game.id, team_id: teamId,
      });
      const picks = await getMySurvivorPicks(SEASON_ID, numericPoolId);
      setMyPicks(picks);
      await loadWeek(week, activeEntryId);
      setNotice("Pick saved.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to save pick.");
    } finally {
      setSaving(false);
    }
  }

  async function handleAddEntry() {
    setSaving(true);
    setError("");
    try {
      const created = await createEntry(SEASON_ID, numericPoolId, null);
      const myEntries = await getMyEntries(SEASON_ID, numericPoolId);
      setEntries(myEntries);
      // Jump straight to the new entry so the next pick applies to it.
      setActiveEntryId(created.id);
      await loadWeek(week, created.id);
      setNotice(`Entry #${created.entry_no} added.`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to add entry.");
    } finally {
      setSaving(false);
    }
  }

  async function handleBuyBack() {
    setSaving(true);
    setError("");
    try {
      await buyBackEntry(SEASON_ID, numericPoolId, activeEntryId, week);
      const myEntries = await getMyEntries(SEASON_ID, numericPoolId);
      setEntries(myEntries);
      await loadWeek(week, activeEntryId);
      setNotice("You're back in.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Buy-back failed.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Box minH="100vh" bg={PAGE_BLUE} color="white">
        <Flex minH="100vh" align="center" justify="center" direction="column" gap={4}>
          <Spinner size="xl" thickness="4px" /><Text>Loading picks...</Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white">
      <PageHeader
        title="SURVIVOR PICKS"
        onBack={() => navigate(`/nfl-survivor/current/${numericPoolId}`, { state: { seasonId: SEASON_ID } })}
        user={user}
        onLogout={() => { logout(); navigate("/login", { replace: true }); }}
      />

      <Container maxW="container.xl" py={8}>
        {error && <Alert status="error" borderRadius="md" mb={4}><AlertIcon />{error}</Alert>}
        {notice && (
          // The page wrapper sets color="white", which the Alert inherits —
          // white on Chakra's light green is barely readable.
          <Alert status="success" borderRadius="md" mb={4} color={TOP_BAR_BLUE}>
            <AlertIcon />{notice}
          </Alert>
        )}

        <Flex justify="flex-start" mb={4}>
          <Button size="sm" bg="white" color={TOP_BAR_BLUE} _hover={{ bg: "gray.100" }}
                  onClick={() => navigate(`/nfl-survivor/current/${numericPoolId}`, { state: { seasonId: SEASON_ID } })}>
            Return to Leaderboard
          </Button>
        </Flex>

        <Flex align="stretch" gap={{ base: 4, md: 6 }} flexWrap="wrap">
        <Box flex="2" minW={{ base: "100%", lg: "560px" }} maxW={{ base: "100%", lg: "calc(100% - 384px)" }}
             bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg"
             boxShadow="lg" p={{ base: 4, md: 6 }}>
          <Flex align="center" justify="space-between" flexWrap="wrap" gap={3} mb={4}>
            <HStack spacing={3} flexWrap="wrap">
              {pool && entries.length < (pool.max_entries_per_user ?? 1) && (
                <Button size="sm" variant="outline" borderColor={TOP_BAR_BLUE} color={TOP_BAR_BLUE}
                        _hover={{ bg: "gray.50" }} isLoading={saving} onClick={handleAddEntry}>
                  + Add Entry ({entries.length}/{pool.max_entries_per_user})
                </Button>
              )}
              <Select size="sm" maxW="140px" value={week ?? ""}
                      onChange={(e) => { const w = Number(e.target.value); setWeek(w); loadWeek(w, activeEntryId); }}>
                {Array.from({ length: maxWeek }, (_, i) => i + 1).map((w) => (
                  <option key={w} value={w}>Week {w}</option>
                ))}
              </Select>
            </HStack>

            <HStack spacing={2}>
              {activeEntry?.is_eliminated ? (
                <Badge colorScheme="red">Eliminated wk{activeEntry.eliminated_week}</Badge>
              ) : (
                <Badge colorScheme="green">Alive</Badge>
              )}
            </HStack>
          </Flex>

          {activeEntry?.is_eliminated && (
            <Alert status="warning" borderRadius="md" mb={4}>
              <AlertIcon />
              <Box flex="1">
                <Text fontSize="sm">This entry is out.</Text>
                {buyback?.allowed ? (
                  <Button size="xs" mt={2} colorScheme="green" isLoading={saving} onClick={handleBuyBack}>
                    Buy back in
                  </Button>
                ) : (
                  <Text fontSize="xs" color="gray.600">{buyback?.reason}</Text>
                )}
              </Box>
            </Alert>
          )}

          {!weekData?.games?.length ? (
            <Text color="gray.600">No games scheduled for week {week}.</Text>
          ) : (
            <VStack align="stretch" spacing={3}>
              {/*
                Instruction plus the entry switcher. These buttons replaced the
                dropdown, so they list EVERY entry — not just the ones still
                needing a pick — otherwise there'd be no way back to an entry
                you've already picked for in order to change it.
              */}
              <Flex align="center" gap={2} flexWrap="wrap">
                {!activeEntry?.is_eliminated && (
                  <Text fontSize="sm" fontWeight="bold" color={TOP_BAR_BLUE}>
                    Select a logo to make your Week {week} pick
                    {entries.length > 1 ? " for these entries:" : ""}
                  </Text>
                )}
                {entries.length > 1 && entries.map((e) => {
                  const done = myPicks.some((p) => p.entry_id === e.id && p.week === week);
                  const isActive = e.id === activeEntryId;
                  return (
                    <Button
                      key={e.id}
                      size="xs"
                      variant={isActive ? "solid" : "outline"}
                      colorScheme={e.is_eliminated ? "red" : done ? "green" : "orange"}
                      onClick={() => { setActiveEntryId(e.id); loadWeek(week, e.id); }}
                    >
                      {e.label || `#${e.entry_no}`}
                      {e.is_eliminated ? " (out)" : done ? " ✓" : ""}
                    </Button>
                  );
                })}
              </Flex>

              {/* Confirms the active entry's pick once it's in. */}
              {!activeEntry?.is_eliminated && pickForWeek && (
                <HStack spacing={2} color="green.700">
                  {pickForWeek.team?.logo_url && (
                    <Image src={pickForWeek.team.logo_url} boxSize="20px" objectFit="contain" />
                  )}
                  <Text fontSize="sm" fontWeight="bold">
                    {entries.length > 1 ? `${activeEntry?.label || `Entry #${activeEntry?.entry_no}`}: ` : ""}
                    {pickForWeek.team?.name}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    — select another logo to change it
                  </Text>
                </HStack>
              )}

              <Flex flexWrap="wrap" gap={{ base: 3, md: 4 }}>
                {weekData.games.map((g) => {
                  const picked = pickForWeek?.team?.id;
                  const awayUsed = g.away_team && !availableSet.has(g.away_team.id) && picked !== g.away_team.id;
                  const homeUsed = g.home_team && !availableSet.has(g.home_team.id) && picked !== g.home_team.id;
                  const lockedOut = g.is_locked || activeEntry?.is_eliminated || saving;
                  const hasPickHere = picked === g.away_team?.id || picked === g.home_team?.id;

                  return (
                    <Box
                      key={g.id}
                      w={{ base: "calc(50% - 6px)", md: "calc(50% - 8px)" }}
                      borderWidth="2px"
                      borderColor={hasPickHere ? "#d69e2e" : "gray.200"}
                      borderRadius="md"
                      bg="white"
                      boxShadow={hasPickHere ? "md" : "sm"}
                      p={{ base: 2, md: 3 }}
                      opacity={g.is_locked && !hasPickHere ? 0.6 : 1}
                    >
                      {/* Date/time left, final score right */}
                      <Flex justify="space-between" align="flex-start" mb={1} gap={2}>
                        <Text fontSize={{ base: "0.6rem", md: "xs" }} color="gray.500" lineHeight="1.3">
                          {fmtKickoff(g.kickoff_utc)}
                        </Text>
                        {g.is_final ? (
                          <Text fontSize={{ base: "0.6rem", md: "xs" }} fontWeight="bold"
                                color="gray.700" whiteSpace="nowrap">
                            {g.away_score}-{g.home_score} F
                          </Text>
                        ) : g.is_locked ? (
                          <Badge colorScheme="gray" fontSize="0.55rem">Locked</Badge>
                        ) : null}
                      </Flex>

                      <Flex align="flex-start" justify="center" gap={{ base: 1, md: 2 }}>
                        <TeamPick
                          team={g.away_team}
                          homeAway="Away"
                          selected={picked === g.away_team?.id}
                          used={awayUsed}
                          disabled={lockedOut || awayUsed}
                          odds={g.odds?.away_win}
                          onSelect={() => handlePick(g, g.away_team.id)}
                        />
                        <Text fontSize={{ base: "2xs", md: "xs" }} fontWeight="bold"
                              color="gray.500" alignSelf="center" pt={3}>
                          @
                        </Text>
                        <TeamPick
                          team={g.home_team}
                          homeAway="Home"
                          selected={picked === g.home_team?.id}
                          used={homeUsed}
                          disabled={lockedOut || homeUsed}
                          odds={g.odds?.home_win}
                          onSelect={() => handlePick(g, g.home_team.id)}
                        />
                      </Flex>

                      {/* Sits under the odds row so it doesn't fight the score */}
                      <Flex justify="center" mt={1}>
                        <Link
                          fontSize="0.65rem"
                          color={g.is_locked ? "gray.400" : TOP_BAR_BLUE}
                          textDecoration={g.is_locked ? "none" : "underline"}
                          cursor={g.is_locked ? "not-allowed" : "pointer"}
                          aria-disabled={g.is_locked || undefined}
                          _hover={g.is_locked ? { textDecoration: "none" } : undefined}
                          onClick={g.is_locked ? undefined : () => setHistoryGame(g)}
                        >
                          Matchup History
                        </Link>
                      </Flex>
                    </Box>
                  );
                })}
              </Flex>

              {weekData.bye_teams?.length > 0 && (
                <Box pt={2}>
                  <Text fontSize="xs" color="gray.500" mb={1}>
                    On bye week {week} ({weekData.bye_teams.length}):
                  </Text>
                  <Flex wrap="wrap" gap={2}>
                    {weekData.bye_teams.map((t) => (
                      <HStack key={t.id} spacing={1} opacity={0.5}>
                        {t.logo_url && <Image src={t.logo_url} boxSize="16px" objectFit="contain" />}
                        <Text fontSize="xs">{t.short_name}</Text>
                      </HStack>
                    ))}
                  </Flex>
                </Box>
              )}
            </VStack>
          )}
        </Box>

        {/* ── Current standings, mirroring the PL pick page's table panel ── */}
        <Box flex="1" minW={{ base: "100%", lg: "330px" }} maxW={{ base: "100%", lg: "360px" }}
             bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`} borderRadius="lg"
             boxShadow="lg" p={{ base: 4, md: 5 }}>
          <Heading size="md" color={TOP_BAR_BLUE} mb={3}>Standings</Heading>
          {!standings?.conferences?.length ? (
            <Text color="gray.600" fontSize="sm">No standings available yet.</Text>
          ) : (
            <VStack align="stretch" spacing={4}>
              {standings.conferences.map((conf) => (
                <Box key={conf.conference}>
                  <Text fontSize="sm" fontWeight="bold" color={TOP_BAR_BLUE} mb={2}
                        borderBottom="2px solid" borderColor={TOP_BAR_BLUE} pb={1}>
                    {conf.conference}
                  </Text>
                  {conf.divisions.map((div) => (
                    <Box key={div.division} mb={3}>
                      <Text fontSize="0.65rem" color="gray.500" textTransform="uppercase"
                            letterSpacing="0.5px" mb={1}>
                        {div.division}
                      </Text>
                      <Table size="sm" variant="simple">
                        <Thead>
                          <Tr>
                            <Th px={1} py={1} fontSize="0.6rem">Team</Th>
                            <Th px={1} py={1} fontSize="0.6rem" isNumeric>W-L</Th>
                            {/* PF/PA only have room from lg up */}
                            <Th px={1} py={1} fontSize="0.6rem" isNumeric
                                display={{ base: "none", lg: "table-cell" }}>PF</Th>
                            <Th px={1} py={1} fontSize="0.6rem" isNumeric
                                display={{ base: "none", lg: "table-cell" }}>PA</Th>
                            <Th px={1} py={1} fontSize="0.6rem" isNumeric>Strk</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {div.teams.map((row) => (
                            <Tr key={row.team.id}>
                              <Td px={1} py={1} borderColor="gray.100">
                                <HStack spacing={2}>
                                  {row.team.logo_url && (
                                    <Image src={row.team.logo_url} boxSize="16px" objectFit="contain" />
                                  )}
                                  <Text fontSize="xs">{row.team.short_name}</Text>
                                </HStack>
                              </Td>
                              <Td px={1} py={1} isNumeric borderColor="gray.100">
                                <Text fontSize="xs" whiteSpace="nowrap">
                                  {row.wins}-{row.losses}{row.ties ? `-${row.ties}` : ""}
                                </Text>
                              </Td>
                              <Td px={1} py={1} isNumeric borderColor="gray.100"
                                  display={{ base: "none", lg: "table-cell" }}>
                                <Text fontSize="xs" color="gray.600">{row.points_for}</Text>
                              </Td>
                              <Td px={1} py={1} isNumeric borderColor="gray.100"
                                  display={{ base: "none", lg: "table-cell" }}>
                                <Text fontSize="xs" color="gray.600">{row.points_against}</Text>
                              </Td>
                              <Td px={1} py={1} isNumeric borderColor="gray.100">
                                <Text fontSize="0.65rem"
                                      color={(row.streak || "").startsWith("W") ? "green.600" : "gray.500"}>
                                  {row.streak || ""}
                                </Text>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  ))}
                </Box>
              ))}
            </VStack>
          )}
        </Box>
        </Flex>
      </Container>

      <MatchupHistoryModal
        isOpen={!!historyGame}
        onClose={() => setHistoryGame(null)}
        teamA={historyGame?.away_team}
        teamB={historyGame?.home_team}
        fetcher={getNflMatchupHistory}
        weekPrefix="Wk"
        drawLabel="TIE"
      />
    </Box>
  );
}
